import {
    AfterContentChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';

@Component({
    selector: 'preopen-accordion',
    templateUrl: './preopen-accordion.component.html'
})
export class PreopenAccordionComponent implements OnInit, AfterViewInit, AfterContentChecked, OnChanges {
    @Input() header: string = '';
    @Input() expanded: boolean = false;
    @Input() initialMaxHeight: string = '150px';
    @Input() useModusIcons: boolean = false; // Whether to use Modus icons instead of text

    @Output() expandStateChange = new EventEmitter<boolean>();

    @ViewChild('accordionContent', { static: false }) accordionContent: ElementRef;

    isExpanded: boolean = false;
    contentMaxHeight: string = '';
    expandedHeight: string = '500px'; // This will be updated dynamically on updateExpandedHeight()
    hasOverflow: boolean = false; // Indicates if content exceeds initialMaxHeight

    constructor(private changeDetectorRef: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.isExpanded = this.expanded;
        this.updateMaxHeight();
    }

    ngAfterViewInit(): void {
        // Calculate initial height and update state
        this.updateExpandedHeight();
        this.updateMaxHeight();
        this.changeDetectorRef.detectChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.expanded) {
            this.isExpanded = this.expanded;
            this.updateMaxHeight();
        }
    }

    // Private flag to prevent excessive checking
    private previousContentHeight: number = 0;

    ngAfterContentChecked(): void {
        // Only recalculate if content has been rendered and height might have changed
        if (this.accordionContent) {
            const currentHeight = this.accordionContent.nativeElement.scrollHeight;

            // Only update if the height has changed
            if (currentHeight !== this.previousContentHeight && currentHeight > 0) {
                this.previousContentHeight = currentHeight;
                this.updateExpandedHeight();

                this.changeDetectorRef.detectChanges();
            }
        }
    }

    /**
     * Calculate the actual content height for better animation
     * and determine if content overflows the initialMaxHeight
     */
    updateExpandedHeight(): void {
        if (this.accordionContent) {
            const contentHeight = this.accordionContent.nativeElement.scrollHeight;

            // Only proceed if we have valid content height
            if (contentHeight > 0) {
                this.expandedHeight = `${contentHeight + 20}px`; // Add some buffer for padding/margins

                // Parse initialMaxHeight to a number (remove 'px')
                const initialHeightStr = this.initialMaxHeight.replace('px', '');
                const initialHeight = parseInt(initialHeightStr, 10);

                // Check if content exceeds initialMaxHeight
                const previousOverflowState = this.hasOverflow;
                this.hasOverflow = contentHeight > initialHeight;

                // Update max height if overflow state changed
                if (previousOverflowState !== this.hasOverflow) {
                    this.updateMaxHeight();
                }
            }
        }
    }

    /**
     * Update the max height based on expanded state
     */
    updateMaxHeight(): void {
        this.contentMaxHeight = this.isExpanded ? this.expandedHeight : this.initialMaxHeight;
    }

    /**
     * Toggle expanded state and update height
     */
    toggleExpand(): void {
        // Make sure we have the correct expanded height before toggling
        if (!this.isExpanded) {
            this.updateExpandedHeight();
        }

        this.isExpanded = !this.isExpanded;
        this.updateMaxHeight();
        this.expandStateChange.emit(this.isExpanded);
    }

    get toggleLabel(): string {
        return this.isExpanded ? 'TC.Todo.ShowLessOptions' : 'Common.ShowMore';
    }
}
